import { Row } from 'app/design/primitives/box';
import { createTheme as createDSTheme } from 'app/shopify';
import { Calendar } from 'app/design/primitives/calendar';
import { Text } from 'app/design/primitives/text';
import { useFormContext } from 'app/hooks/form';
import { isBefore, isSameDay } from 'app/lib/date';
import { isPublicServiceCategory } from 'app/network/category';
import type { CreateListingDTO } from 'app/types/listing';
import { ListingType } from 'app/types/listing';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useWatch } from 'react-hook-form';
import { IconAlertTriangle } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
const theme = createDSTheme();

export function DateSection({ disabled = false, initial }: { disabled?: boolean; initial?: Date }) {
  const { control, setValue, trigger } = useFormContext<CreateListingDTO>();
  const { t } = useTranslation();

  const [watchedType, serviceCategoryId, watchedEnd, watchedStart] = useWatch({
    control,
    exact: true,
    name: ['type', 'serviceCategoryId', 'end', 'start'],
  });

  useEffect(() => {
    setValue('start', null);
    setValue('end', null);
  }, [setValue]);

  if (watchedType === ListingType.offer) return null;

  const isPublicServiceRequest = isPublicServiceCategory(serviceCategoryId);
  const initialDate = initial ?? new Date();

  const onDatePress = (date: Date) => {
    const dateAsISOString = date.toISOString();
    const watchedEndAsDate = watchedEnd ? new Date(watchedEnd) : null;
    const watchedStartAsDate = watchedStart ? new Date(watchedStart) : null;

    const isSameAsEnd = watchedEndAsDate && isSameDay(date, watchedEndAsDate);
    const isSameAsStart = watchedStartAsDate && isSameDay(date, watchedStartAsDate);

    if (isPublicServiceRequest) {
      // Only select one date
      if (isSameAsStart) {
        setValue('start', null, { shouldDirty: true });
        setValue('end', null, { shouldDirty: true });
      } else {
        setValue('start', dateAsISOString, { shouldDirty: true });
        setValue('end', dateAsISOString, { shouldDirty: true });
      }
      return;
    }

    if (isSameAsStart) {
      /**
       * When pressing on the `start` date,
       * we must clear it and therefore `end`.
       */
      setValue('start', null, { shouldDirty: true });
      setValue('end', null, { shouldDirty: true });
    } else if (isSameAsEnd) {
      /**
       * When clicking on the `end` date,
       * we must reset it to `start` to allow for
       * picking a single date.
       */
      setValue('end', watchedStart, { shouldDirty: true });
    } else if (!watchedStartAsDate) {
      /**
       * When no date was selected before pressing,
       * we initialize both `start` and `end` to the
       * given value to allow for picking a single date.
       */
      setValue('start', dateAsISOString, { shouldDirty: true });
      setValue('end', dateAsISOString, { shouldDirty: true });
    } else if (isBefore(date, watchedStartAsDate)) {
      /**
       * When pressing on date that's before `start`,
       * we move `start` back without touching `end`.
       */
      setValue('start', dateAsISOString, { shouldDirty: true });
    } else {
      setValue('end', dateAsISOString, { shouldDirty: true });
    }

    /**
     * This is needed because we're not using the `Controller`
     * API to drive this part of the form.
     */
    void Promise.all([trigger('start'), trigger('end')]);
  };

  return (
    <>
      <Row gap="spacing.general.x1" marginBottom="spacing.general.x2">
        <Text color="text.primary" fontWeight="700" variant="bodyBase">
          {t(`dateSection.title${isPublicServiceRequest ? 'One' : ''}`, { ns: 'new' })}
        </Text>
        {!isPublicServiceRequest ? (
          <Text color="text.primary" variant="bodyBase">
            ({t('optionales', { ns: 'common' })})
          </Text>
        ) : null}
      </Row>

      {disabled ? (
        <Row
          backgroundColor="surface.brand.secondary-2.default"
          borderColor="text.warning"
          borderRadius="radius.lg"
          borderWidth={1}
          marginBottom="spacing.general.x4"
          padding="spacing.general.x4"
        >
          <Row flex={1}>
            <IconAlertTriangle strokeWidth={ICON_STROKE_WIDTH} color={theme.colors['icon.warning']} />
          </Row>

          <Text flex={7} fontWeight="500">
            {t('disabled', { ns: 'calendar' })}
          </Text>
        </Row>
      ) : null}

      <Calendar
        startFromNow
        disabled={disabled}
        {...{ onDatePress }}
        daysSelectionMode={isPublicServiceRequest ? 'individual' : 'interval'}
        initial={initialDate}
        selected={[watchedStart, watchedEnd]
          .filter((date): date is string => Boolean(date))
          .map((date) => new Date(date))}
      />
    </>
  );
}
